import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      }
      const formUrl = 'https://readdy.ai/api/form/d9q4t14nlsngrm4llrh0';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 北条麻妃电影全集精选',
            description: aboutData.brandIntro,
            publisher: {
              '@type': 'Organization',
              name: aboutData.brandName,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hojomaki-film.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://hojomaki-film.com/about' },
            ],
          }),
        }}
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-950">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-background-50">
                关于我们
              </h1>
              <p className="mt-3 text-background-400 text-sm max-w-2xl">
                {aboutData.brandIntro}
              </p>
            </div>
          </div>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    品牌故事
                  </h2>
                  <div className="mt-6 space-y-4 text-foreground-700 leading-relaxed text-sm">
                    {aboutData.story.split('\n\n').map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                  <div className="mt-8 p-5 bg-primary-50 rounded-lg border border-primary-200/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <i className="ri-quote-text"></i>
                      </div>
                      <p className="text-foreground-800 font-medium text-sm italic">
                        {aboutData.mission}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20film%20archive%20library%20with%20wooden%20shelves%20filled%20with%20film%20reels%20and%20movie%20posters%2C%20warm%20soft%20lighting%2C%20vintage%20cinema%20aesthetic%2C%20organized%20and%20curated%20collection%2C%20professional%20archival%20space%2C%20sophisticated%20atmosphere&width=800&height=600&seq=about-story&orientation=landscape"
                    alt="品牌故事"
                    title="北条麻妃电影全集精选 - 品牌故事"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  影迷口碑
                </h2>
                <p className="mt-2 text-foreground-600 text-sm">
                  来自影迷的真实评价
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {aboutData.reputation.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg p-6"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      "{item.comment}"
                    </p>
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/70">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <div className="font-medium text-foreground-900 text-sm">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.date}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  联系我们
                </h2>
                <p className="mt-2 text-foreground-600 text-sm">
                  有任何问题或建议？我们期待听到您的声音
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-foreground-900 text-sm">电子邮箱</h3>
                  <p className="mt-1 text-xs text-foreground-600">{aboutData.contact.email}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-foreground-900 text-sm">微信</h3>
                  <p className="mt-1 text-xs text-foreground-600">{aboutData.contact.wechat}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-foreground-900 text-sm">响应时间</h3>
                  <p className="mt-1 text-xs text-foreground-600">{aboutData.contact.responseTime}</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-6 md:p-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 outline-none focus:border-primary-300 transition-colors duration-200"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                    placeholder="请输入您想说的话，最多500字"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-secondary-400 text-background-50 font-medium text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送消息'}
                </button>

                {formStatus === 'success' && (
                  <div className="mt-4 p-4 bg-accent-50 border border-accent-200/50 rounded-lg text-sm text-accent-700 text-center">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    消息已成功发送！我们会在{aboutData.contact.responseTime}内回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-4 bg-accent-50 border border-accent-200/50 rounded-lg text-sm text-accent-700 text-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError || '提交失败，请稍后重试'}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}